import { useEffect, useMemo, useState } from "react";

import { AlertTriangle, CalendarPlus, Check } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import { useApplyRoll, useRollPreview, type DriverPlan } from "../api";
import { fmtUsd, weekLabel } from "../lib";

/** One editable proposal. Keyed by driver + week so an edit survives a refetch. */
type Draft = Record<string, { include: boolean; amount: number }>;

const keyOf = (driverId: string, week: string) => `${driverId}|${week}`;

/** Rolling the horizon forward — the weekly maintenance job that used to need a
 *  developer and a migration.
 *
 *  Everything is previewed and nothing is inferred silently: each driver's
 *  rhythm is read from its own history, a driver whose rhythm cannot be read is
 *  listed with the reason instead of being skipped, and every amount can be
 *  changed or dropped before it is written. */
export function RollHorizonDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const { data, isLoading } = useRollPreview(open);
  const apply = useApplyRoll();
  const [draft, setDraft] = useState<Draft>({});

  const proposals = useMemo(
    () => (data?.plans ?? []).filter((p) => p.weeks.length > 0),
    [data],
  );
  const blocked = useMemo(
    () => (data?.plans ?? []).filter((p) => p.blocked),
    [data],
  );
  const covered = useMemo(
    () => (data?.plans ?? []).filter((p) => !p.blocked && p.weeks.length === 0),
    [data],
  );

  // Seed every proposal as included at its own suggested amount.
  useEffect(() => {
    if (!open) return;
    const next: Draft = {};
    proposals.forEach((p) =>
      p.weeks.forEach((w) => {
        next[keyOf(p.driver_id, w.week_ending)] = { include: true, amount: w.amount };
      }),
    );
    setDraft(next);
  }, [open, proposals]);

  const selected = Object.entries(draft).filter(([, v]) => v.include);

  const submit = () => {
    const weeks = selected.map(([k, v]) => {
      const [driver_id = "", week_ending = ""] = k.split("|");
      return { driver_id, week_ending, amount: v.amount };
    });
    apply.mutate(weeks, { onSuccess: () => onOpenChange(false) });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Roll the horizon forward</DialogTitle>
          <DialogDescription>
            {data?.cutover && data?.horizon_end ? (
              <>
                Actuals run through {weekLabel(data.cutover, true)}, so the model needs
                drivers scheduled through {weekLabel(data.horizon_end, true)}. Each item
                below repeats its own rhythm, read from its history. Nothing is written
                until you confirm.
              </>
            ) : (
              "No bank data yet, so there is no horizon to roll to."
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[26rem] overflow-y-auto">
          {isLoading && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Reading each driver's rhythm…
            </p>
          )}

          {!isLoading && proposals.length === 0 && (
            <p className="rounded-md border border-success/40 bg-success-light p-3 text-sm">
              Every driver already reaches the horizon. Nothing to roll.
            </p>
          )}

          {proposals.map((p) => (
            <PlanRows key={p.driver_id} plan={p} draft={draft} setDraft={setDraft} />
          ))}

          {blocked.length > 0 && (
            <div className="mt-4 rounded-md border border-warning/40 bg-warning-light p-3">
              <p className="mb-1 flex items-center gap-1.5 text-sm font-medium">
                <AlertTriangle className="h-4 w-4" />
                Needs a human — no rhythm to read
              </p>
              <ul className="space-y-1 pl-1 text-xs text-muted-foreground">
                {blocked.map((p) => (
                  <li key={p.driver_id}>
                    <span className="font-medium text-foreground">{p.label}</span> — {p.blocked}{" "}
                    Add its next week on the drivers table below.
                  </li>
                ))}
              </ul>
            </div>
          )}

          {covered.length > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              {covered.length} driver{covered.length === 1 ? "" : "s"} already reach the
              horizon.
            </p>
          )}
        </div>

        {apply.isError && (
          <p className="rounded-md border border-danger/40 bg-danger-light p-3 text-sm text-danger">
            {apply.error.message}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={selected.length === 0 || apply.isPending}>
            <Check className="h-4 w-4" />
            {apply.isPending
              ? "Writing…"
              : `Add ${selected.length} week${selected.length === 1 ? "" : "s"}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PlanRows({
  plan,
  draft,
  setDraft,
}: {
  plan: DriverPlan;
  draft: Draft;
  setDraft: (fn: (prev: Draft) => Draft) => void;
}) {
  return (
    <div className="border-b py-3 last:border-0">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">{plan.label}</span>
        <Badge variant="neutral">{plan.bucket}</Badge>
        <Badge variant="info">{plan.cadence}</Badge>
        <span className="text-xs text-muted-foreground">
          covered through {plan.covered_through ? weekLabel(plan.covered_through, true) : "—"}
        </span>
      </div>
      <div className="flex flex-col gap-1.5">
        {plan.weeks.map((w) => {
          const k = keyOf(plan.driver_id, w.week_ending);
          const row = draft[k] ?? { include: true, amount: w.amount };
          return (
            <div key={k} className="flex items-center gap-3 pl-1">
              <Checkbox
                checked={row.include}
                onCheckedChange={(v) =>
                  setDraft((prev) => ({ ...prev, [k]: { ...row, include: Boolean(v) } }))
                }
                aria-label={`Include ${plan.label} for ${w.week_ending}`}
              />
              <span className="w-28 text-sm">{weekLabel(w.week_ending, true)}</span>
              <Input
                type="number"
                step="0.01"
                value={row.amount}
                onChange={(e) =>
                  setDraft((prev) => ({
                    ...prev,
                    [k]: { ...row, amount: Number(e.target.value) },
                  }))
                }
                className="h-8 w-36"
                aria-label={`Amount for ${plan.label} ${w.week_ending}`}
              />
              <span className="text-xs text-muted-foreground">{fmtUsd(row.amount)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** The button that opens it. Lives on the Forecast drivers card. */
export function RollHorizonButton({ disabled }: { disabled?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" disabled={disabled} onClick={() => setOpen(true)}>
        <CalendarPlus className="h-4 w-4" />
        Roll to horizon
      </Button>
      <RollHorizonDialog open={open} onOpenChange={setOpen} />
    </>
  );
}
